import { Emitter } from "@core/events/emitter";
import type { IAuthModel } from "@models/auth/IAuthModel";
import type { IMultiuserSessionModel, MultiuserParticipant } from "@models/multiuser/IMultiuserSessionModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { ProjectInfo } from "@models/data/projectInfo";
import type { HttpAdminStorageModel } from "@models/storage/HttpAdminStorageModel";
import type { IStorageModel } from "@models/storage/IStorageModel";
import {
  ACCOUNT_ROLE,
  SHARED_LINK_STATE,
  SHARED_USER_ROLE,
  isSharedLinkVisited,
  sharedLinkState,
} from "@models/data/sharedLinkDto";
import { generateSharedLinkId } from "@models/data/sharedLinkId";
import type { ICustomerModel } from "@models/customer/ICustomerModel";
import { CAMPFIRE_PLAN_TYPE, isPaidPlan } from "@models/data/campfirePlanType";
import type { UserDataExt } from "@models/data/userDataExt";
import { emptyUserDataExt } from "@models/data/userDataExt";
import type { AccountRole, SharedUserRole } from "@models/data/userRolePermissions";
import type { ChangeSharingEvent } from "@models/data/changeSharingWire";
import { CHANGE_SHARING_EVENT } from "@models/data/changeSharingWire";
import {
  accountRoleToSharedUserRole,
  canAuthorProject as roleCanAuthorProject,
  canModifyProject as roleCanModifyProject,
  canShareProject as roleCanShareProject,
  canUseTool as roleCanUseTool,
  isProjectOwner,
  sharedUserRoleToAccountRole,
} from "@models/data/userRolePermissions";
import type {
  IPermissionsModel,
  PermissionsEditorMode,
  RoleLoadState,
} from "./IPermissionsModel";
import { PERMISSIONS_EDITOR_MODE } from "./IPermissionsModel";

/**
 * `PermissionsModel` — port of Unity's `Models/PermissionsModel.cs` plus the
 * per-project role half of `CustomerModel` (`LoadCurrentUserRole`,
 * `GetCurrentUserRole`, `SetUserProjectRole`) and the role state Unity keeps
 * on its mutable `UserInfo`.
 *
 * WHY THE ROLE STATE LIVES HERE AND NOT ON `UserInfo`: Unity's `UserInfo` is a
 * mutable god-object whose `m_effectiveRole` is rewritten on project entry and
 * reset on exit, and forgetting the reset leaks a sharee's downgraded role
 * into their own projects. The web's `UserInfo` is a frozen DTO handed out by
 * `onSignedIn`, so mutating it would be both wrong and invisible to
 * reactivity. Identity (who you are) stays on `UserInfo`; authorization (what
 * you may do, here, now) lives on this model. That also keeps `models/auth`
 * free of a storage dependency.
 *
 * ⚠ UPSTREAM BUGS DELIBERATELY NOT PORTED:
 *  1. `UpdateUIPermissions()` re-subscribes `SharingChanged` on EVERY call
 *     (`:120-124`) while `Start` already subscribed (`:189`) and `OnDestroy`
 *     unsubscribes once (`:209`). It runs on every participant join / leave /
 *     presenter change, so the handler multiplies and each duplicate can
 *     re-fire `SendChangeSharingEvent` (`:262`). We subscribe exactly once.
 *  2. Two null-derefs: `UpdateUIPermissions` null-checks the multiuser model
 *     at `:121` then dereferences it unconditionally at `:125`, and
 *     `CanUseGizmos` dereferences `GetSelf()` at `:331` although its sibling
 *     explicitly handles a null self at `:126`. Both guarded.
 *  3. `Awake` registers the model before `Start` resolves its dependencies, so
 *     a predicate called in between NREs. Here every getter answers from safe
 *     defaults until {@link attachTransport} lands — which is also the state
 *     it is in while the first role fetch is in flight.
 */
export class PermissionsModel implements IPermissionsModel {
  readonly onPermissionsChanged = new Emitter<void>();
  readonly onAccessRevoked = new Emitter<{ projectId: string }>();
  readonly onProjectRoleChanged = new Emitter<{ projectId: string; role: SharedUserRole }>();

  private m_auth: IAuthModel | null = null;
  private m_multiuser: IMultiuserSessionModel | null = null;
  private m_project: IProjectModel | null = null;
  private m_adminStorage: HttpAdminStorageModel | null = null;
  private m_customer: ICustomerModel | null = null;
  private m_storage: IStorageModel | null = null;

  private m_editorMode: PermissionsEditorMode = PERMISSIONS_EDITOR_MODE.Viewer;
  /** The natural-account profile. Seeded to the LEAST privilege. */
  private m_userData: UserDataExt = emptyUserDataExt("");
  private m_roleLoadState: RoleLoadState = "idle";
  /** `UserInfo.m_effectiveRole` — per project; reset to natural on exit. */
  private m_effectiveRole: AccountRole = ACCOUNT_ROLE.Reader;
  /** `UserInfo.m_canBePresenter` — per project. */
  private m_canBePresenter = false;
  /**
   * TWO separate generation counters, and they must stay separate. The
   * account-profile fetch and the per-project role resolution are independent
   * async operations that routinely overlap — a project opens while the
   * profile is still in flight. Sharing one counter makes each cancel the
   * other: the profile resolves, sees a bumped counter, and DISCARDS itself,
   * so the natural account role is never stored and every non-owner reads as
   * Viewer forever. (Caught by the harness, not by review.)
   */
  private m_profileSeq = 0;
  private m_roleSeq = 0;
  /**
   * And a THIRD for the project-plan fetch. It used to read `m_roleSeq`, which
   * `loadCurrentUserRoleAsync` has already bumped by the time this runs — so
   * the guard could never fire and a stale plan could land on a new project.
   */
  private m_planSeq = 0;
  /**
   * `ProjectInfo.PlanType` for the OPEN project — resolved on project load,
   * never mutated onto the readonly `ProjectInfo` DTO the list model owns.
   * Seeded Unknown, which is NOT paid.
   */
  private m_activeProjectPlanType: number = CAMPFIRE_PLAN_TYPE.Unknown;

  private readonly m_offFns: Array<() => void> = [];

  // -- Lifecycle ---------------------------------------------------------

  /**
   * Hand the model its collaborators once they exist. Registered in the CORE
   * pass (auth / storage / multiuser are registered much further down
   * `registrations.ts`), so — like `AppSettingsModel` — it must be USABLE
   * before this call: every predicate below answers from the seeded defaults
   * until then. Do NOT reorder the passes to avoid the two-phase setup.
   */
  attachTransport(deps: {
    auth: IAuthModel;
    multiuser: IMultiuserSessionModel;
    project: IProjectModel;
    adminStorage: HttpAdminStorageModel | null;
    customer: ICustomerModel | null;
    /** For `FindAndApplySharedLink` on an inbound `ProjectShared`. */
    storage?: IStorageModel | null;
  }): void {
    this.m_storage = deps.storage ?? null;
    this.m_auth = deps.auth;
    this.m_multiuser = deps.multiuser;
    this.m_project = deps.project;
    this.m_adminStorage = deps.adminStorage;
    this.m_customer = deps.customer;

    // Models REACT to auth; they never poll (`src/models/auth/CLAUDE.md:11`).
    this.m_offFns.push(this.m_auth.onSignedIn.on(() => { void this.refreshAccountProfileAsync(); }));
    // ⚠ `onSignedIn` fires ONLY for an interactive sign-in — `initializeAsync`
    // restores a cached session with `emitSignedIn: false`. Without this second
    // subscription the account profile is never fetched on a page RELOAD, and
    // every user is pinned to the seeded Reader for the life of the tab: the
    // grid's Drafts tab becomes unreachable, and a non-owner can never present
    // and therefore never author. `registrations.ts:698` subscribes exactly
    // this way for events / multiuser / AI, and this model was the one new
    // participant that omitted it.
    this.m_offFns.push(this.m_auth.onInitialized.on(() => {
      if (!this.m_auth?.currentUser) return;
      void this.refreshAccountProfileAsync();
    }));
    this.m_offFns.push(this.m_auth.onSignedOut.on(() => this.clearAccountProfile()));
    this.m_offFns.push(this.m_auth.onSessionExpired.on(() => this.clearAccountProfile()));

    // Unity recomputes on session start, presenter change, participant join
    // and participant leave. All four already exist on the web roster.
    this.m_offFns.push(this.m_multiuser.onPresenterChanged.on(() => this.updateUIPermissions()));
    this.m_offFns.push(this.m_multiuser.onParticipantJoined.on(() => this.updateUIPermissions()));
    this.m_offFns.push(this.m_multiuser.onParticipantsChanged.on(() => this.updateUIPermissions()));

    // Project load: Unity raises `PermissionsChanged` here WITHOUT recomputing
    // (`:297-300`). That is an upstream oversight, not a contract — the
    // editor mode genuinely can change with the project, so we recompute.
    this.m_offFns.push(
      this.m_project.onActiveProjectChanged.on((info) => {
        void this.onActiveProjectChangedAsync(info);
      }),
    );

    if (this.m_auth.currentUser) void this.refreshAccountProfileAsync();
    this.updateUIPermissions();
  }

  dispose(): void {
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
    this.onPermissionsChanged.clear();
    this.onAccessRevoked.clear();
    this.onProjectRoleChanged.clear();
  }

  // -- State -------------------------------------------------------------

  get editorMode(): PermissionsEditorMode { return this.m_editorMode; }
  get effectiveRole(): AccountRole { return this.m_effectiveRole; }
  get naturalAccountRole(): AccountRole { return this.m_userData.accountRole; }
  get roleLoadState(): RoleLoadState { return this.m_roleLoadState; }

  /**
   * `ProjectModelSlim.ActiveUserRole` (`:149-162`), verbatim and in order:
   * no project → Viewer; you created it → Owner; otherwise the effective
   * account role mapped across.
   *
   * The ownership re-test is not redundant: `sharedUserRoleToAccountRole`
   * collapses Owner→Writer, so an `AccountRole` can never BE Owner and
   * ownership has to be recovered from `creatorId` here.
   */
  get activeUserRole(): SharedUserRole {
    const project = this.m_project?.activeProject ?? null;
    if (!project) return SHARED_USER_ROLE.Viewer;
    if (isProjectOwner(project, this.m_auth?.currentUser?.userId)) return SHARED_USER_ROLE.Owner;
    return accountRoleToSharedUserRole(this.m_effectiveRole);
  }

  // -- Editor mode -------------------------------------------------------

  /** The local roster row — Unity's `MultiuserAppModel.GetSelf()`. */
  private getSelf(): MultiuserParticipant | null {
    const multiuser = this.m_multiuser;
    if (!multiuser) return null;
    const key = multiuser.localParticipantKey;
    if (!key) return null;
    return multiuser.participants.find((p) => p.key === key) ?? null;
  }

  /**
   * `UpdateUIPermissions()` (`:112-158`) in Unity's exact order. Emits ONLY on
   * a real transition, which is what keeps the version-ref mirrors in the
   * ViewModel layer from re-rendering on every heartbeat.
   */
  updateUIPermissions(): void {
    let next: PermissionsEditorMode = PERMISSIONS_EDITOR_MODE.Viewer;
    const self = this.getSelf();
    if (!self) {
      next = PERMISSIONS_EDITOR_MODE.Rightless;
    } else if (self.isPresenter) {
      next = PERMISSIONS_EDITOR_MODE.Presenter;
    } else if (this.m_effectiveRole === ACCOUNT_ROLE.Commenter) {
      next = PERMISSIONS_EDITOR_MODE.Commenter;
    } else if (this.m_effectiveRole === ACCOUNT_ROLE.Writer) {
      next = PERMISSIONS_EDITOR_MODE.Writer;
    }

    if (next !== this.m_editorMode) {
      this.m_editorMode = next;
      this.onPermissionsChanged.emit();
    }
  }

  private get isPresenting(): boolean {
    return this.m_editorMode === PERMISSIONS_EDITOR_MODE.Presenter;
  }

  // -- Predicates --------------------------------------------------------

  canBePresenter(): boolean {
    return this.m_canBePresenter;
  }

  canUseTool(tool: number): boolean {
    return roleCanUseTool(this.activeUserRole, tool, this.isPresenting);
  }

  canSelectScene(): boolean {
    return this.isPresenting && this.canBePresenter();
  }

  canAuthorProject(): boolean {
    return this.isPresenting && roleCanAuthorProject(this.activeUserRole);
  }

  canUseGizmos(isCuttingPlaneSelected: boolean): boolean {
    if (this.canAuthorProject()) return true;
    // Upstream: `m_multiuserAppModel.GetSelf().IsPresenter && … tag == Slice`.
    // Guarded — upstream NREs when there is no local participant.
    return this.getSelf()?.isPresenter === true && isCuttingPlaneSelected;
  }

  canChangeVisibility(): boolean {
    return this.isPresenting;
  }

  canBeAuthor(): boolean {
    return roleCanAuthorProject(this.activeUserRole);
  }

  canShareProject(): boolean {
    return roleCanShareProject(this.activeUserRole);
  }

  canModifyProject(): boolean {
    return this.isPresenting && roleCanModifyProject(this.activeUserRole);
  }

  isRightless(): boolean {
    return this.m_editorMode === PERMISSIONS_EDITOR_MODE.Rightless;
  }

  /**
   * `CanUsePaidFeatures()` — a property of the PROJECT, not of the user
   * (`PermissionsModel.cs:359-362` → `ProjectInfo.IsProjectPaid`). A free user
   * inside an enterprise project gets paid features; an enterprise user
   * inside someone's free project does not.
   */
  canUsePaidFeatures(): boolean {
    return isPaidPlan(this.m_activeProjectPlanType);
  }

  isEffectiveFreeUser(): boolean {
    const project = this.m_project?.activeProject ?? null;
    return isProjectOwner(project, this.m_auth?.currentUser?.userId) && !this.hasCoreLicense();
  }

  /** `UserInfo.HasCoreLicense()` (`:177-180`) — BOTH terms, not either. */
  hasCoreLicense(): boolean {
    return this.m_effectiveRole === ACCOUNT_ROLE.Writer
      && this.m_userData.accountRole === ACCOUNT_ROLE.Writer;
  }

  // -- Account profile ---------------------------------------------------

  /**
   * Fetch the natural-account profile once per sign-in.
   *
   * A FAILURE IS NOT "READER". Caching the seeded default as though the server
   * had answered would lock a Writer out of their own project permanently with
   * nothing on screen, so a failed fetch leaves {@link roleLoadState} at
   * `"failed"` and the value retryable
   * (`Campfire_Web_CloudData.md:35` — never silently swallow).
   */
  private async refreshAccountProfileAsync(): Promise<void> {
    const auth = this.m_auth;
    const admin = this.m_adminStorage;
    const user = auth?.currentUser ?? null;
    if (!auth || !admin || !user || !user.customerId) return;

    const seq = ++this.m_profileSeq;
    this.m_roleLoadState = "loading";
    try {
      const data = await admin.getUserDataAsync(user.customerId, user.userId);
      if (seq !== this.m_profileSeq) return;
      this.m_userData = data;
      this.m_roleLoadState = "loaded";
      // Outside a project the effective role IS the natural one
      // (`UserInfo` ctor, `:352-353`). Inside one, re-resolve — and test the
      // LIVE project, not `m_resolvedForProjectId`: a project that opened
      // while this fetch was in flight has an in-flight role resolution of its
      // own that computed against the SEEDED profile, and has not yet stamped
      // that marker. Re-resolving bumps `m_roleSeq`, which is what makes the
      // stale one drop itself instead of landing last and pinning every
      // non-owner to Viewer for the session.
      if (!this.m_project?.activeProject) {
        this.resetEffectiveRole();
        this.updateUIPermissions();
        this.onPermissionsChanged.emit();
      } else {
        await this.loadCurrentUserRoleAsync();
      }
    } catch (err) {
      if (seq !== this.m_profileSeq) return;
      this.m_roleLoadState = "failed";
      console.warn(
        "[PermissionsModel] could not resolve the account role; permissions stay at the "
        + "least-privilege default and will retry on the next sign-in or project load:",
        err,
      );
    }
  }

  private clearAccountProfile(): void {
    this.m_profileSeq++;
    this.m_roleSeq++;
    this.m_userData = emptyUserDataExt("");
    this.m_roleLoadState = "idle";
    this.resetEffectiveRole();
    this.updateUIPermissions();
    this.onPermissionsChanged.emit();
  }

  // -- Per-project role --------------------------------------------------

  private async onActiveProjectChangedAsync(info: ProjectInfo | null): Promise<void> {
    if (!info) {
      // Back on the grid: `ProjectsViewModel.cs:1868`.
      //
      // Bumping the role generation is load-bearing: a role resolution for the
      // project we are LEAVING can still be in flight, and without this it
      // lands after the reset and re-pins the user to the role they had in a
      // project they have closed.
      this.m_roleSeq++;
      this.m_planSeq++;
      this.m_activeProjectPlanType = CAMPFIRE_PLAN_TYPE.Unknown;
      this.resetEffectiveRole();
      this.updateUIPermissions();
      this.onPermissionsChanged.emit();
      return;
    }
    // ⚠ RESOLVE THE ROLE FIRST, THEN REPAIR THE PROFILE — never the other way
    // round. For a project you CREATED the role is settled by
    // `resolveCurrentUserRoleAsync` branch (b) with no network at all, and
    // with the full presenter gate an unresolved role means the owner has no
    // tools, no gizmos and no scene switching. Awaiting an unrelated
    // account-profile fetch first put exactly that read-only state in front of
    // the owner for as long as AppSync took to answer — or to time out.
    await this.loadCurrentUserRoleAsync();
    // A profile fetch that FAILED leaves every predicate at least-privilege,
    // and opening a project is the natural moment to try again — otherwise a
    // single transient failure at boot locks the whole tab out until a full
    // sign-out. `roleLoadState` stays `"failed"` precisely so this can retry.
    // The second role load is what applies the repaired profile; it is cheap,
    // idempotent, and its generation counter supersedes the first.
    if (this.m_roleLoadState === "failed" || this.m_roleLoadState === "idle") {
      await this.refreshAccountProfileAsync();
      await this.loadCurrentUserRoleAsync();
    }
    await this.loadCurrentProjectPlanTypeAsync(info);
  }

  /**
   * `CustomerModel.LoadCurrentProjectPlanType()` (`:1924-1963`): your own
   * project, or one in your own account, uses YOUR account's plan; a project
   * shared from another account is asked about directly. Any failure falls
   * back to IndividualFree — fail closed to FREE.
   */
  private async loadCurrentProjectPlanTypeAsync(info: ProjectInfo | null): Promise<void> {
    if (!info) {
      this.m_activeProjectPlanType = CAMPFIRE_PLAN_TYPE.Unknown;
      return;
    }
    const myCustomerId = this.m_auth?.currentUser?.customerId ?? null;
    const sharedLinkId = info.sharedLinkId ?? "";
    const seq = ++this.m_planSeq;

    if (!sharedLinkId || !info.customerId || info.customerId === myCustomerId) {
      if (seq !== this.m_planSeq) return;
      this.m_activeProjectPlanType =
        this.m_customer?.customerInfo?.planType ?? CAMPFIRE_PLAN_TYPE.Unknown;
      this.onPermissionsChanged.emit();
      return;
    }

    let resolved: number = CAMPFIRE_PLAN_TYPE.IndividualFree;
    try {
      const plan = await this.m_adminStorage?.getProjectOwnerPlanAsync(
        info.customerId,
        sharedLinkId,
      );
      if (plan) resolved = plan.planType;
    } catch (err) {
      console.warn("[PermissionsModel] project owner plan unavailable; treating as free:", err);
    }
    if (seq !== this.m_planSeq) return;
    this.m_activeProjectPlanType = resolved;
    this.onPermissionsChanged.emit();
  }

  /**
   * `CustomerModel.LoadCurrentUserRole()` → `GetCurrentUserRole()`
   * (`:1978-2052`), in Unity's exact branch order.
   */
  async loadCurrentUserRoleAsync(signal?: AbortSignal): Promise<void> {
    const project = this.m_project?.activeProject ?? null;
    const user = this.m_auth?.currentUser ?? null;
    const seq = ++this.m_roleSeq;

    const resolved = await this.resolveCurrentUserRoleAsync(project, user, signal);
    if (seq !== this.m_roleSeq) return;

    this.m_effectiveRole = sharedUserRoleToAccountRole(resolved.role);
    this.m_canBePresenter = resolved.canBePresenter;
    this.updateUIPermissions();
    this.onPermissionsChanged.emit();
  }

  private async resolveCurrentUserRoleAsync(
    project: ProjectInfo | null,
    user: { userId: string } | null,
    signal?: AbortSignal,
  ): Promise<{ role: SharedUserRole; canBePresenter: boolean }> {
    // (a) Not authenticated. Unity's branch here returns (Editor, true) for
    // its Spark-tutorial path, which has no web counterpart — so we FAIL
    // CLOSED instead, mirroring `CanCurrentUserWrite()` returning false when
    // there is no user (`AIGuardrailsModel.Permission.cs:60`).
    if (!user) return { role: SHARED_USER_ROLE.Viewer, canBePresenter: false };

    // (b) No project, or you created it → Owner, presenter-eligible.
    if (!project || isProjectOwner(project, user.userId)) {
      return { role: SHARED_USER_ROLE.Owner, canBePresenter: true };
    }

    // (c) No shared link → the user's OWN account role wins. Upstream's
    // comment: "The user's role overrides the account role in Asterix
    // release". Note it deliberately does NOT consult the project row's
    // `AccountRole`; that attribute decides whether the project is VISIBLE to
    // the account at all, which the grid already handles.
    const sharedLinkId = project.sharedLinkId ?? "";
    if (!sharedLinkId) {
      return {
        role: accountRoleToSharedUserRole(this.m_userData.accountRole),
        canBePresenter: this.m_userData.canBePresenter,
      };
    }

    // (d) A shared link → its role, with the obsolete Presenter downgraded.
    let role = project.sharedUserRole ?? SHARED_USER_ROLE.Viewer;
    if (role === SHARED_USER_ROLE.Presenter) role = SHARED_USER_ROLE.Commenter;

    // (e) The sharee's OWN account licence clamps the link role
    // (`CustomerModel.cs:2023-2047`). A null result — transport failure —
    // SKIPS the clamp, exactly as upstream does.
    const admin = this.m_adminStorage;
    let maxAppRole: string | null = null;
    let maxCanBePresenter: boolean | null = null;
    if (admin && project.customerId) {
      try {
        const max = await admin.getMaxAccountRoleAsync(project.customerId, sharedLinkId, signal);
        if (max) {
          maxAppRole = max.appRole;
          maxCanBePresenter = max.canBePresenter;
          role = clampRoleByAccountLicence(role, max.appRole);
        }
      } catch (err) {
        console.warn("[PermissionsModel] maxperms clamp unavailable; using the link role:", err);
      }
    }
    // ⚠ THE ELIGIBILITY DISJUNCTION SITS OUTSIDE THE FETCH, exactly as
    // upstream (`CustomerModel.cs:2044`). Computing it inside `if (max)` — the
    // obvious shape — means a maxperms failure silently strips presentership
    // from an Editor whose LINK ROLE alone already entitles them to it, and
    // since a non-presenter cannot author, that turns a transport hiccup into
    // a read-only session with no way back.
    const canBePresenter = maxCanBePresenter === true
      || maxAppRole === "Writer"
      || role === SHARED_USER_ROLE.Editor;
    return { role, canBePresenter };
  }

  /**
   * `PermissionsModel.OnServerSharingChanged` (`:225-300`) — someone changed
   * this project's sharing while the user is inside it.
   *
   * Returns whether the caller should also refresh the projects grid.
   *
   * ⚠ THE OWN-ECHO GUARD LIVES HERE AND ONLY HERE. `change_sharing` is on
   * Unity's never-ignore whitelist, so the sender receives its own event and
   * FOUR of the five upstream consumers process it deliberately (to refresh
   * their own grid and share sheet). Only this one skips it — otherwise the
   * sharer would raise a "your permissions changed" dialog at themselves.
   */
  applyChangeSharing(evt: ChangeSharingEvent): void {
    const project = this.m_project?.activeProject ?? null;
    const user = this.m_auth?.currentUser ?? null;
    if (!project || !user) return;
    if (project.id !== evt.projectId) return;
    // Unity guards on the USER, not the app instance, so this user's change
    // in another tab is skipped too.
    if (evt.originatingUserId && evt.originatingUserId === user.userId) return;

    if (evt.shareEvent === CHANGE_SHARING_EVENT.AccountRoleChanged) {
      // Addresses the whole account — `userId` is deliberately empty here.
      const newRole = evt.content;
      // Unity writes this UNCONDITIONALLY and BEFORE the guard below
      // (`PermissionsModel.cs:239`), because it is the value a later
      // `ProjectUnshared` falls back to. Skipping it when the guard rejects
      // leaves the next event reading a stale account role.
      this.m_project?.applyProjectSharingChange(project.id, { accountRole: newRole });
      // ⚠ AN INDIVIDUAL SHARED LINK OVERRIDES THE ACCOUNT GRANT. Upstream's
      // own comment admits the flaw it carries: `HasSharedLink` only knows
      // the project was OPENED with a link, not whether the link has since
      // been deleted.
      const hasSharedLink = (project.sharedLinkId ?? "") !== "";
      const currentRole = this.activeUserRole;
      if (hasSharedLink || currentRole === SHARED_USER_ROLE.Owner || newRole === currentRole) {
        return;
      }
      if (newRole === SHARED_USER_ROLE.NoAccess) {
        // Unity routes straight back to the grid, silently, with NO dialog
        // (`:250-254`). Two eviction paths exist and they are not
        // interchangeable — this is the one that fires when a project is
        // un-shared out from under you.
        this.onAccessRevoked.emit({ projectId: project.id });
        return;
      }
      this.setUserProjectRole(newRole);
      this.onProjectRoleChanged.emit({ projectId: project.id, role: newRole });
      // Tell peers to update this user's roster row (`:262-268`). The
      // re-broadcast is emitted by the RECEIVER, not by the sharer.
      this.m_project?.publishLiveProjectEvent("change_sharing", {
        userId: user.userId,
        projId: project.id,
        actId: user.customerId ?? "",
        shareEvt: CHANGE_SHARING_EVENT.ParticipantUpdate,
        content: this.activeUserRole,
      });
      return;
    }

    // The three PERSON-addressed events.
    const isMine = evt.userId === user.userId;
    if (!isMine) return;
    if (
      evt.shareEvent !== CHANGE_SHARING_EVENT.UserRoleChanged
      && evt.shareEvent !== CHANGE_SHARING_EVENT.ProjectShared
      && evt.shareEvent !== CHANGE_SHARING_EVENT.ProjectUnshared
    ) {
      return;
    }
    let newRole = evt.content;
    if (evt.shareEvent === CHANGE_SHARING_EVENT.ProjectShared) {
      // `CustomerModel.FindAndApplySharedLink()` (`:281`): the link's id is
      // DERIVED, not fetched — `GenerateID(projectId, myEmail)` — so it is one
      // point read on the current user's own account partition. Unity awaits
      // it before applying the role; the role does not depend on the result,
      // so the web lets it settle on its own and applies the role now.
      void this.findAndApplySharedLinkAsync(project, user.email).catch((err) => {
        console.warn("[PermissionsModel] FindAndApplySharedLink failed:", err);
      });
    }
    if (evt.shareEvent === CHANGE_SHARING_EVENT.ProjectUnshared) {
      // The link is gone; you fall back to whatever the account grants
      // (`:283-287`). Note that may still be a valid grant — being un-shared
      // is not automatically being evicted. Unity clears `SharedLinkId` here
      // (`:277`), which is what makes a subsequent account-wide change apply
      // to this user at all (the guard above tests exactly that field).
      this.m_project?.applyProjectSharingChange(project.id, { sharedLinkId: null });
      newRole = project.accountRole ?? SHARED_USER_ROLE.Viewer;
    }
    if (newRole === SHARED_USER_ROLE.NoAccess) {
      this.onAccessRevoked.emit({ projectId: project.id });
      return;
    }
    // NO dialog here. Unity calls `OpenPermissionsChangedDialog()` in exactly
    // ONE place — inside the AccountRoleChanged branch (`:257`) — and the three
    // person-addressed events run `SetUserProjectRole` + `UpdateUIPermissions`
    // silently (`:281-288`). Emitting the same event from both branches raised
    // a modal upstream never shows, including on the `ParticipantUpdate`
    // re-broadcast that a downgraded peer emits at everyone.
    this.setUserProjectRole(newRole);
  }

  /**
   * `CustomerModel.FindAndApplySharedLink` (`:2186-2196`) — "the shared link
   * needs to be applied to the current project if a user switches from account
   * sharing to individual user sharing". Reads `GenerateID(projectId, email)`
   * from the CURRENT USER's account (`CustomerInfo.ID`), and when the row
   * exists records it on the open project and marks it Visited on the
   * PROJECT's account — Unity's two partitions, verbatim; they coincide for
   * every same-account share.
   */
  private async findAndApplySharedLinkAsync(project: ProjectInfo, email: string): Promise<void> {
    const storage = this.m_storage;
    const myCustomerId = this.m_auth?.currentUser?.customerId ?? null;
    if (!storage || !storage.isOnline || !myCustomerId || !email) return;
    const sharedLinkId = generateSharedLinkId(project.id, email.toLowerCase());
    const link = await storage.getSharedLink(myCustomerId, sharedLinkId);
    if (!link) return;
    this.m_project?.applyProjectSharingChange(project.id, { sharedLinkId });
    if (isSharedLinkVisited(link)) return;
    await storage.updateSharedLinkState(
      project.customerId || myCustomerId,
      sharedLinkId,
      sharedLinkState(link) | SHARED_LINK_STATE.Visited,
    );
  }

  /** `CustomerModel.SetUserProjectRole` — an inbound sharing change. */
  setUserProjectRole(role: SharedUserRole): void {
    // Supersede any in-flight role resolution. Without this an inbound
    // `change_sharing` that lands DURING project load is silently overwritten
    // moments later by the resolution that was already running — the user
    // keeps the role they were just moved off.
    this.m_roleSeq++;
    this.m_effectiveRole = sharedUserRoleToAccountRole(role);
    this.updateUIPermissions();
    this.onPermissionsChanged.emit();
  }

  resetEffectiveRole(): void {
    this.m_effectiveRole = this.m_userData.accountRole;
    this.m_canBePresenter = this.m_userData.canBePresenter;
  }
}

/**
 * `CustomerModel.GetCurrentUserRole` step (e) — the licence ceiling a sharee's
 * own account puts on their link role. An unrecognised `appRole` (including
 * `"Writer"`) applies no clamp.
 */
function clampRoleByAccountLicence(role: SharedUserRole, appRole: string | null): SharedUserRole {
  switch (appRole) {
    case "Reader":
      return SHARED_USER_ROLE.Viewer;
    case "Commenter":
      return role === SHARED_USER_ROLE.Editor
        || role === SHARED_USER_ROLE.Presenter
        || role === SHARED_USER_ROLE.Commenter
        ? SHARED_USER_ROLE.Commenter
        : role;
    case "Presenter":
      return role === SHARED_USER_ROLE.Editor || role === SHARED_USER_ROLE.Presenter
        ? SHARED_USER_ROLE.Commenter
        : role;
    default:
      return role;
  }
}
